import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  OnInit,
} from "@angular/core";
import * as _ from "underscore";
import { AdjudicationService } from "../../../services/adjudication.service";
import { TagService } from "../../../services/tag.service";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import {
  Adjudication,
  BlrEyes,
  MactelDiagnosis,
} from "../../../models/adjudication.model";
import { Patient } from "../../../models/patient.model";
import { Tag } from "../../../models/tag.model";
import { ModalComponent } from "../../../components/modal/modal.component";
import { ToastService } from "../../../components/toast/toast.service";
import { DataService } from "../../../services/data.service";
import { ImageService } from "../../../services/image.service";
import { MatSelectChange } from "@angular/material";
import { ImagePackage } from "../../../models/image-package.model";
import { LoadingSpinnerService } from "../../../components/loading-spinner/loading-spinner.service";
import { Note } from "../../../models/note.model";

@Component({
  selector: "lowy-adjudicate",
  templateUrl: "./adjudication-modal.component.html",
  providers: [
    AdjudicationService,
    TagService,
    DataService,
    ImageService,
    LoadingSpinnerService,
  ],
})
export class AdjudicateComponent implements OnInit {
  public adjudicationForm: FormGroup;
  public adjudication: Adjudication;
  public mactelDiagnoses;
  public blrEyes;
  public patientId: string;
  public imagePackage: ImagePackage;
  public currentPatientTags: Tag[];
  public pendingPatientTags: Tag[];
  public allPatientTags: Tag[];
  public meta: any; // misc meta data for patient
  public uploadComments: string[];
  public notes: Note[];

  @ViewChild("modal") modal: ModalComponent;

  @Output() saved: EventEmitter<any>;

  patientDataTypeId: number;

  constructor(
    private adjudicationService: AdjudicationService,
    private toastService: ToastService,
    private tagService: TagService,
    private dataService: DataService,
    private imageService: ImageService,
    private loadingSpinnerService: LoadingSpinnerService,
    private formBuilder: FormBuilder
  ) {
    this.buildForm();
    this.saved = new EventEmitter();
    this.mactelDiagnoses = MactelDiagnosis;
    this.blrEyes = BlrEyes;
    this.pendingPatientTags = [];
  }

  async ngOnInit() {
    const allDataTypes = await this.dataService.getDataTypes();
    const patientDataType = _.filter(
      allDataTypes,
      (dataType) => dataType.abbr === "patient"
    );
    this.patientDataTypeId = patientDataType[0].dataTypeId;
    const allTags = await this.tagService.getTags();
    this.allPatientTags = _.filter(
      allTags,
      (tag) => tag.dataTypeId === this.patientDataTypeId
    );
  }

  async buildForm() {
    this.adjudicationForm = this.formBuilder.group({
      mactelDiagnosis: ["", Validators.required],
      subStudy: [""],
      gradingEligible: [false],
      blrPattern: [false],
      blrEye: [""],
      reviewer: [""],
      comments: [""],
    });
  }

  public async open(imagePackage: ImagePackage) {
    this.modal.open();
    this.patientId = imagePackage.patientId;
    this.imagePackage = imagePackage;
    this.currentPatientTags = await this.tagService.getTagsForData(
      "patient",
      this.patientId
    );
    this.adjudication = await this.adjudicationService.getAdjudication(
      this.patientId
    );
    this.uploadComments = await this.imageService.getAllUploadComments(
      this.patientId
    );
    if (this.adjudication && this.adjudication.adjudicationId) {
      this.adjudicationForm.setValue({
        mactelDiagnosis: this.adjudication.mactelDiagnosis,
        subStudy: this.adjudication.subStudy,
        gradingEligible: this.adjudication.gradingEligible,
        blrPattern: this.adjudication.blrPattern,
        blrEye: this.adjudication.blrEye ? this.adjudication.blrEye : null,
        reviewer: this.adjudication.reviewer
          ? this.adjudication.reviewer
          : null,
        comments: null,
      });
    } else {
      this.adjudicationForm.setValue({
        mactelDiagnosis: null,
        subStudy: null,
        gradingEligible: false,
        blrPattern: false,
        blrEye: null,
        reviewer: null,
        comments: null,
      });
    }
    this.meta = {};
    this.meta["studyId"] = await this.dataService.getSingleMetadata(
      "image",
      this.patientId,
      "IMAGES",
      "STUDY_ID"
    );
    this.meta["imageNotes"] = await this.dataService.getSingleMetadata(
      "image",
      this.patientId,
      "IMAGES",
      "COMMENTS"
    );
    this.meta["uploadNotes"] = await this.dataService.getSingleMetadata(
      "image",
      this.patientId,
      "IMAGES",
      "UPLOAD_COMMENTS"
    );
    this.meta["imageTypes"] = await this.imageService.getAllImageTypes(
      this.patientId
    );

    // Family Members auto get grading checkbox checked
    if (this.adjudication.familyMember === "Family Member") {
      const gradingEligibleControl =
        this.adjudicationForm.get("gradingEligible");
      gradingEligibleControl.patchValue(true);
    }
  }

  public mactelDiagnosisChange(e: MatSelectChange) {
    if (e.value === "yes") {
      const control = this.adjudicationForm.get("gradingEligible");
      control.patchValue(true);
    }
  }

  public async submit() {
    this.loadingSpinnerService.start();
    try {
      const adjudicationToSave: Adjudication = this.adjudicationForm.value;
      adjudicationToSave.patientId = this.patientId;
      // save adjudication
      if (this.adjudication && this.adjudication.adjudicationId) {
        adjudicationToSave.adjudicationId = this.adjudication.adjudicationId;
        await this.adjudicationService.updateAdjudication(
          this.adjudication.adjudicationId,
          adjudicationToSave,
          this.imagePackage.visitId,
          this.imagePackage.studyId
        );
      } else {
        adjudicationToSave["visitId"] = this.imagePackage.visitId;
        await this.adjudicationService.createAdjudication(
          adjudicationToSave,
          this.imagePackage.visitId,
          this.imagePackage.studyId
        );
      }
      // save tags
      for (let i = 0; i < this.pendingPatientTags.length; i++) {
        const payload = {
          tagId: this.pendingPatientTags[i].tagId,
          dataId: this.patientId,
          dataTypeId: this.patientDataTypeId,
        };
        await this.tagService.attachTag(payload);
      }
      this.pendingPatientTags = [];
      this.saved.emit();
      this.modal.close();
      this.loadingSpinnerService.stop();
      this.toastService.throwToast("Diagnosis saved");
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.pendingPatientTags = [];
      this.toastService.throwToast("Error saving adjudication: " + err);
    }
  }

  public cancel() {
    this.pendingPatientTags = [];
    this.modal.close();
  }

  addTags(tags: Array<Tag>) {
    tags.forEach((tag) => {
      let doAdd = true; // ensure not a dupe
      for (let i = 0; i < this.pendingPatientTags.length; i++) {
        if (this.pendingPatientTags[i].tagId === tag.tagId) {
          doAdd = false;
        }
      }

      if (doAdd) {
        this.pendingPatientTags.push(tag);
      }
    });
  }

  deleteTag(tag: Tag) {
    for (let i = 0; i < this.pendingPatientTags.length; i++) {
      if (this.pendingPatientTags[i].tagId === tag.tagId) {
        // delete selected
        this.pendingPatientTags.splice(i, 1);
        return;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/home/grading/adjudication/adjudication-modal.component.ts